import React, { useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import useElementToPdf from '../utils/useElementToPdf';
import { 
  Printer, 
  Shield, 
  Check, 
  X, 
  User, 
  Users, 
  DollarSign, 
  Package, 
  Calendar, 
  Settings, 
  FileText,
  Home,
  Clock,
  Bell,
  BarChart3,
  Building2,
  Star,
  HardDrive,
  Mail,
  Activity
} from 'lucide-react';

const UserGuide = () => {
  const { user } = useAuth();
  const printRef = useRef();

  const handlePrint = useElementToPdf({
    contentRef: printRef,
    documentTitle: 'دليل المستخدم - KE Gents Saloon',
    pageStyle: `
      @page {
        size: A4;
        margin: 15mm;
      }
      @media print {
        body { 
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: white !important;
          color: black !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `
  });

  const getRoleName = (role) => {
    const roles = {
      'admin': 'مدير النظام',
      'barber': 'حلاق',
      'guest': 'زائر'
    };
    return roles[role] || role;
  };

  const getRoleDescription = (role) => {
    const descriptions = {
      'admin': 'لديك صلاحيات كاملة لإدارة جميع أقسام النظام بما في ذلك إدارة المستخدمين والإعدادات.',
      'barber': 'يمكنك إدارة إيراداتك ومصروفاتك الخاصة والتعامل مع قائمة الانتظار.',
      'guest': 'لديك صلاحيات محدودة للعرض فقط.'
    };
    return descriptions[role] || '';
  };

  // تعريف الصلاحيات حسب الدور
  const permissions = {
    admin: {
      dashboard: { view: true, description: 'لوحة التحكم الرئيسية' },
      revenues: { view: true, add: true, edit: true, delete: true, description: 'إدارة الإيرادات' },
      expenses: { view: true, add: true, edit: true, delete: true, description: 'إدارة المصروفات' },
      barbers: { view: true, add: true, edit: true, delete: true, description: 'إدارة الحلاقين' },
      users: { view: true, add: true, edit: true, delete: true, description: 'إدارة المستخدمين' },
      inventory: { view: true, add: true, edit: true, delete: true, description: 'إدارة المخزون' },
      queue: { view: true, add: true, edit: true, delete: true, description: 'قائمة الانتظار' },
      appointments: { view: true, add: true, edit: true, delete: true, description: 'المواعيد' },
      salaries: { view: true, add: true, edit: true, delete: true, description: 'الرواتب' },
      reports: { view: true, print: true, description: 'التقارير' },
      settings: { view: true, edit: true, description: 'الإعدادات' },
      analytics: { view: true, description: 'تحليلات الزوار' },
      housing: { view: true, add: true, edit: true, delete: true, description: 'سكن الموظفين' },
      ratings: { view: true, description: 'التقييمات' },
      activityLog: { view: true, description: 'سجل النشاطات' },
      backup: { view: true, create: true, description: 'النسخ الاحتياطي' },
      email: { view: true, send: true, description: 'إدارة البريد' },
      advance: { view: true, edit: true, description: 'السلفة' }
    },
    barber: {
      dashboard: { view: true, description: 'لوحة التحكم الرئيسية' },
      revenues: { view: true, add: true, edit: 'own_1h', delete: false, description: 'إدارة الإيرادات (إيراداتك فقط)' },
      expenses: { view: true, add: true, edit: 'own_1h', delete: false, description: 'إدارة المصروفات (مصروفاتك فقط)' },
      barbers: { view: true, add: false, edit: false, delete: false, description: 'عرض الحلاقين' },
      users: { view: false, add: false, edit: false, delete: false, description: 'إدارة المستخدمين' },
      inventory: { view: true, add: true, edit: false, delete: false, description: 'إدارة المخزون' },
      queue: { view: true, add: false, edit: 'own', delete: false, description: 'قائمة الانتظار (عملائك فقط)' },
      appointments: { view: true, add: false, edit: false, delete: false, description: 'المواعيد' },
      salaries: { view: 'own', add: false, edit: false, delete: false, description: 'الرواتب (راتبك فقط)' },
      reports: { view: 'own', print: true, description: 'التقارير (تقاريرك فقط)' },
      settings: { view: 'deposit_only', edit: 'propose', description: 'الإعدادات (اقتراح العربون فقط)' },
      analytics: { view: false, description: 'تحليلات الزوار' },
      housing: { view: 'assigned', add: false, edit: false, delete: false, description: 'سكن الموظفين' },
      ratings: { view: 'own', description: 'التقييمات' },
      activityLog: { view: false, description: 'سجل النشاطات' },
      backup: { view: false, create: false, description: 'النسخ الاحتياطي' },
      email: { view: false, send: false, description: 'إدارة البريد' },
      advance: { view: true, edit: false, description: 'السلفة (عرض فقط)' },
      services: { view: true, add: true, edit: 'propose', delete: false, description: 'الخدمات (اقتراح للمدير)' },
      depositSettings: { view: true, edit: 'propose', description: 'إعدادات العربون (اقتراح للمدير)' }
    },
    guest: {
      dashboard: { view: true, description: 'لوحة التحكم الرئيسية' },
      revenues: { view: true, add: false, edit: false, delete: false, description: 'عرض الإيرادات' },
      expenses: { view: true, add: false, edit: false, delete: false, description: 'عرض المصروفات' },
      barbers: { view: true, add: false, edit: false, delete: false, description: 'عرض الحلاقين' },
      users: { view: false, add: false, edit: false, delete: false, description: 'إدارة المستخدمين' },
      inventory: { view: true, add: false, edit: false, delete: false, description: 'عرض المخزون' },
      queue: { view: true, add: false, edit: false, delete: false, description: 'عرض قائمة الانتظار' },
      appointments: { view: true, add: false, edit: false, delete: false, description: 'عرض المواعيد' },
      salaries: { view: false, add: false, edit: false, delete: false, description: 'الرواتب' },
      reports: { view: true, print: false, description: 'عرض التقارير' },
      settings: { view: false, edit: false, description: 'الإعدادات' },
      analytics: { view: false, description: 'تحليلات الزوار' },
      housing: { view: false, add: false, edit: false, delete: false, description: 'سكن الموظفين' },
      ratings: { view: true, description: 'عرض التقييمات' },
      activityLog: { view: false, description: 'سجل النشاطات' },
      backup: { view: false, create: false, description: 'النسخ الاحتياطي' },
      email: { view: false, send: false, description: 'إدارة البريد' },
      advance: { view: true, edit: false, description: 'السلفة (عرض فقط)' }
    }
  };

  const userPermissions = permissions[user?.role] || permissions.guest;

  const getPermissionIcon = (section) => {
    const icons = {
      dashboard: <Home className="w-5 h-5" />,
      revenues: <DollarSign className="w-5 h-5" />,
      expenses: <DollarSign className="w-5 h-5" />,
      barbers: <Users className="w-5 h-5" />,
      users: <User className="w-5 h-5" />,
      inventory: <Package className="w-5 h-5" />,
      queue: <Clock className="w-5 h-5" />,
      appointments: <Calendar className="w-5 h-5" />,
      salaries: <DollarSign className="w-5 h-5" />,
      reports: <FileText className="w-5 h-5" />,
      settings: <Settings className="w-5 h-5" />,
      analytics: <BarChart3 className="w-5 h-5" />,
      housing: <Building2 className="w-5 h-5" />,
      ratings: <Star className="w-5 h-5" />,
      activityLog: <Activity className="w-5 h-5" />,
      backup: <HardDrive className="w-5 h-5" />,
      email: <Mail className="w-5 h-5" />,
      advance: <DollarSign className="w-5 h-5" />
    };
    return icons[section] || <Shield className="w-5 h-5" />;
  };

  const renderPermissionValue = (value) => {
    if (value === true) {
      return <span className="flex items-center gap-1 text-green-500"><Check className="w-4 h-4" /> نعم</span>;
    }
    if (value === false) {
      return <span className="flex items-center gap-1 text-red-500"><X className="w-4 h-4" /> لا</span>;
    }
    if (value === 'own') {
      return <span className="flex items-center gap-1 text-yellow-500"><Check className="w-4 h-4" /> الخاص فقط</span>;
    }
    if (value === 'own_1h') {
      return <span className="flex items-center gap-1 text-yellow-500"><Check className="w-4 h-4" /> الخاص (ساعة واحدة)</span>;
    }
    if (value === 'assigned') {
      return <span className="flex items-center gap-1 text-yellow-500"><Check className="w-4 h-4" /> المخصص فقط</span>;
    }
    return <span className="text-muted-foreground">-</span>;
  };

  const sections = [
    { key: 'dashboard', name: 'لوحة التحكم' },
    { key: 'revenues', name: 'الإيرادات' },
    { key: 'expenses', name: 'المصروفات' },
    { key: 'advance', name: 'السلفة' },
    { key: 'barbers', name: 'الحلاقين' },
    { key: 'users', name: 'المستخدمين' },
    { key: 'inventory', name: 'المخزون' },
    { key: 'queue', name: 'قائمة الانتظار' },
    { key: 'appointments', name: 'المواعيد' },
    { key: 'salaries', name: 'الرواتب' },
    { key: 'reports', name: 'التقارير' },
    { key: 'analytics', name: 'تحليلات الزوار' },
    { key: 'housing', name: 'سكن الموظفين' },
    { key: 'ratings', name: 'التقييمات' },
    { key: 'activityLog', name: 'سجل النشاطات' },
    { key: 'backup', name: 'النسخ الاحتياطي' },
    { key: 'email', name: 'البريد الإلكتروني' },
    { key: 'settings', name: 'الإعدادات' }
  ];

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-primary">دليل المستخدم</h1>
          <p className="text-muted-foreground mt-1">صلاحياتك في النظام</p>
        </div>
        <Button 
          onClick={handlePrint}
          className="bg-primary hover:bg-primary/80 text-primary-foreground gap-2"
          data-testid="print-guide-btn"
        >
          <Printer className="w-4 h-4" />
          طباعة PDF
        </Button>
      </div>

      {/* Print Content */}
      <div ref={printRef}>
        {/* Print Header - Hidden on screen */}
        <div className="hidden print:block print-header">
          <h1>KE Gents Saloon</h1>
          <p>دليل صلاحيات المستخدم</p>
        </div>

        {/* User Info Card */}
        <Card className="mb-6 bg-gradient-to-br from-card to-card/80 border-primary/20 user-info">
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
                <User className="w-8 h-8 text-primary" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-primary">{user?.name || 'المستخدم'}</h2>
                <div className="flex items-center gap-2 mt-1">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  <span className="text-lg">{getRoleName(user?.role)}</span>
                </div>
                <p className="text-muted-foreground mt-2 text-sm max-w-lg">
                  {getRoleDescription(user?.role)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permissions Table */}
        <Card className="border-white/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              جدول الصلاحيات
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-right py-3 px-4 font-semibold">القسم</th>
                    <th className="text-center py-3 px-4 font-semibold">عرض</th>
                    <th className="text-center py-3 px-4 font-semibold">إضافة</th>
                    <th className="text-center py-3 px-4 font-semibold">تعديل</th>
                    <th className="text-center py-3 px-4 font-semibold">حذف</th>
                  </tr>
                </thead>
                <tbody>
                  {sections.map((section, index) => {
                    const perm = userPermissions[section.key];
                    if (!perm) return null;
                    return (
                      <tr 
                        key={section.key} 
                        className={`border-b border-white/5 ${index % 2 === 0 ? 'bg-secondary/20' : ''}`}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <span className="text-primary">{getPermissionIcon(section.key)}</span>
                            <div>
                              <span className="font-medium">{section.name}</span>
                              {perm.description && (
                                <p className="text-xs text-muted-foreground">{perm.description}</p>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-center">{renderPermissionValue(perm.view)}</td>
                        <td className="py-3 px-4 text-center">{renderPermissionValue(perm.add)}</td>
                        <td className="py-3 px-4 text-center">{renderPermissionValue(perm.edit)}</td>
                        <td className="py-3 px-4 text-center">{renderPermissionValue(perm.delete)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Legend */}
        <Card className="mt-6 border-white/10">
          <CardHeader>
            <CardTitle className="text-lg">دليل الرموز</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-green-500"><Check className="w-4 h-4" /></span>
                <span className="text-sm">مسموح</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-red-500"><X className="w-4 h-4" /></span>
                <span className="text-sm">غير مسموح</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-yellow-500"><Check className="w-4 h-4" /></span>
                <span className="text-sm">الخاص فقط</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1 text-yellow-500"><Clock className="w-4 h-4" /></span>
                <span className="text-sm">خلال ساعة واحدة</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Notes for Role */}
        {user?.role === 'barber' && (
          <Card className="mt-6 border-yellow-500/30 bg-yellow-500/5">
            <CardHeader>
              <CardTitle className="text-yellow-500 flex items-center gap-2">
                <Bell className="w-5 h-5" />
                ملاحظات مهمة للحلاق
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>• يمكنك تعديل إيراداتك ومصروفاتك خلال <strong>ساعة واحدة فقط</strong> من الإضافة.</p>
              <p>• يمكنك اتخاذ إجراء على حجوزات عملائك فقط في قائمة الانتظار.</p>
              <p>• لا يمكنك حذف أي عنصر - الحذف متاح للمدير فقط.</p>
              <p>• المصروفات التي تضيفها تُخصم تلقائياً من السلفة.</p>
            </CardContent>
          </Card>
        )}

        {user?.role === 'admin' && (
          <Card className="mt-6 border-green-500/30 bg-green-500/5">
            <CardHeader>
              <CardTitle className="text-green-500 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                صلاحيات المدير
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p>• لديك صلاحيات كاملة على جميع أقسام النظام.</p>
              <p>• يمكنك تعديل وحذف أي عنصر في أي وقت.</p>
              <p>• يمكنك إدارة المستخدمين وتغيير كلمات المرور.</p>
              <p>• يمكنك عمل نسخ احتياطي على Google Drive.</p>
              <p>• العمليات الحساسة تتطلب رمز OTP للتأكيد.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default UserGuide;
